import { some } from "lodash";
import PROFILE_SECTIONS from "PFApp/profiles/profile_sections";
import { useGrowl } from "PFApp/use_growl";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import AutoSelect from "PFComponents/select/autoselect";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import { parseResponseErrors } from "PFCore/helpers/parse_errors";
import { createCvTemplate, updateCvTemplate } from "PFCore/services/cv_templates";
import { CustomType } from "PFTypes";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Fields } from "./components/fields";
import css from "./cv_template_edit_page.module.scss";

type InlineInputFieldProps = {
  title: string;
  children: ReactNode;
};

const InlineInputField = ({ title, children }: InlineInputFieldProps) => (
  <div className={css.fieldWrapper}>
    <div className={css.fieldTitle}>{title}</div>
    <div className={css.fieldInput}>{children}</div>
  </div>
);

type CvTemplateEditPageProps = {
  cvTemplate: {
    id: number;
    key: string;
    displayAs: string;
    fields: any[];
    default: boolean;
    exportFormat: string;
  };
  accountId: string;
  customTypes: CustomType[];
};

export const CvTemplateEditPage = ({ cvTemplate, customTypes, accountId }: CvTemplateEditPageProps) => {
  const { t } = useTranslation(["saasAdmin", "admin"]);

  const { id } = cvTemplate;
  const isNew = !id;

  const history = useHistory();
  const growl = useGrowl();

  const [key, setKey] = useState(cvTemplate.key);
  const [displayAs, setDisplayAs] = useState(cvTemplate.displayAs);
  const [fields, setFields] = useState(cvTemplate.fields || []);
  const [exportFormat, setExportFormat] = useState(cvTemplate.exportFormat);
  const [isDefault, setIsDefault] = useState(cvTemplate.default || false);
  const [errors, setErrors] = useState<any>({});

  const handleSubmit = () => {
    const data = {
      id,
      key,
      fields,
      displayAs,
      exportFormat,
      default: isDefault,
      accountId
    };

    const apiPromise = isNew ? createCvTemplate(data) : updateCvTemplate(id, data);

    apiPromise
      .then(() => {
        growl({ message: t("saasAdmin:cvTemplates.update.success") });
        history.push(`/saas_admin/accounts/${accountId}/cv_templates`);
      })
      .catch(({ response }) => {
        const parsedErrors = parseResponseErrors(response.data.errors);
        if (parsedErrors?.base) {
          growl({ message: parsedErrors.base, kind: "error" });
        } else {
          growl({ message: t("admin:form.errors.notifyGrowl"), kind: "error" });
          setErrors(parsedErrors);
        }
      });
  };

  const exportFormatOptions = [
    {
      id: "pdf",
      text: "Pdf",
      item: "pdf"
    },
    {
      id: "docx",
      text: "Docx",
      item: "docx"
    },
    {
      id: "csv",
      text: "Csv",
      item: "csv"
    },
    {
      id: "pptx",
      text: "Pptx",
      item: "pptx"
    }
  ];

  const selectedExportFormatOption = exportFormatOptions.find((option) => option.id === exportFormat);

  const exportableCustomTypes = useMemo(
    () =>
      customTypes.filter(({ sections }) =>
        some(sections, (section) =>
          [
            PROFILE_SECTIONS.profile_basic_info,
            PROFILE_SECTIONS.profile_expertise,
            PROFILE_SECTIONS.profile_private_fields,
            PROFILE_SECTIONS.profile_experience_highlights,
            PROFILE_SECTIONS.profile_past_experience
          ].includes(section.key)
        )
      ),

    [customTypes]
  );

  return (
    <div className={css.wrapper}>
      <h1 className={css.header}>{isNew ? `New Template` : `Edit "${key}" Template`}</h1>
      <PageBox style={{ padding: 20 }}>
        <InlineInputField title="Key">
          <InputFieldSet
            value={key}
            onChange={setKey}
            disabled={!isNew}
            error={errors.key || errors["cv_templates/key"]}
          />
        </InlineInputField>

        <InlineInputField title="Display As">
          <InputFieldSet value={displayAs} onChange={setDisplayAs} error={errors.display_as} />
        </InlineInputField>
        <InlineInputField title="Default">
          <Toggle checked={isDefault} onChange={setIsDefault} error={errors.default} />
        </InlineInputField>
        <InlineInputField title="Export Format">
          <AutoSelect
            label=""
            values={selectedExportFormatOption ? [selectedExportFormatOption] : null}
            letCreate={false}
            cache={false}
            query={() => Promise.resolve(exportFormatOptions)}
            handleChange={(values) => setExportFormat(values[0].id)}
            error={errors.export_format}
            multi={false}
            disabled={!isNew}
            closeOnChange
          />
        </InlineInputField>
        <InlineInputField title="Fields">
          <Fields fields={fields} customTypes={exportableCustomTypes} onChange={setFields} />
        </InlineInputField>

        <div className={css.footer}>
          <ButtonLink kind="secondary" href={`/saas_admin/accounts/${accountId}/cv_templates`}>
            Cancel
          </ButtonLink>
          <Button onClick={handleSubmit}>{isNew ? "Create" : "Update"}</Button>
        </div>
      </PageBox>
    </div>
  );
};
